import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Box, Button, Typography} from '@mui/material'
import {ChartFrame, IVypuskChart} from '@containers/monitoringPlatform/passengerCharts/types'
import {estimateTerminals, IPlacedStop} from '@containers/monitoringPlatform/passengerCharts/chart/helpers/estimateTerminals'
import {pickStopLabels} from '@containers/monitoringPlatform/passengerCharts/chart/helpers/pickStopLabels'
import {timeTicks} from '@containers/monitoringPlatform/passengerCharts/chart/helpers/timeTicks'
import {formatTime} from '@containers/monitoringPlatform/passengerCharts/helpers/formatTime'
import sx from '@containers/monitoringPlatform/passengerCharts/chart/sx'

const W = 1400
const H = 420
const L = 40
const R = 10
const TOP = 26
const PH = 230
const Y_MAX = 50
const MIN_SPAN = 600
const EDGE = 300
const NAMES_SPAN = 3 * 3600
const NAMES_WITH_TIME_SPAN = 2 * 3600
const DOTS_SPAN = 3 * 3600
const MIN_CIRCLE_STOPS = 4
const FRAME_GAP = 300
const TAP_DISTANCE = 5
const ACCENT = 'var(--account-accent, #0C6DDF)'

const cleanName = (name: string | null) => (name ?? '—').replace(/\s*\([^)]*\)$/, '')
const yy = (v: number) => TOP + (PH - TOP) * (1 - Math.min(v, Y_MAX) / Y_MAX)

interface ITip {
	left: number
	top: number
	lines: string[]
}

const PassengerChart = ({vypusk}: {vypusk: IVypuskChart}) => {
	const placed = useMemo(() => estimateTerminals(vypusk.stops), [vypusk.stops])
	const passed = useMemo(() => placed.filter((s) => s.realArrival != null), [placed])
	const frames = vypusk.frames

	const bounds = useMemo(() => {
		const times = [
			...passed.map((s) => s.realArrival!),
			...passed.map((s) => s.realDeparture ?? s.realArrival!),
			...frames.map((f) => f[0]),
			...vypusk.vehicles.flatMap((v) => [v.from, v.to]),
		]
		return times.length ? [Math.min(...times) - EDGE, Math.max(...times) + EDGE] : [0, 1]
	}, [passed, frames, vypusk.vehicles])

	const circles = useMemo(() => {
		const byCircle = new Map<number, IPlacedStop[]>()
		passed.forEach((s) => byCircle.set(s.circle, [...(byCircle.get(s.circle) ?? []), s]))
		return Array.from(byCircle.entries()).map(([circle, stops]: [number, IPlacedStop[]]) => ({
			circle,
			count: stops.length,
			from: Math.min(...stops.map((s) => s.realArrival!)),
			to: Math.max(...stops.map((s) => s.realDeparture ?? s.realArrival!)),
		}))
	}, [passed])

	const [view, setView] = useState<[number, number]>([bounds[0], bounds[1]])
	const [tip, setTip] = useState<ITip | null>(null)
	const svgRef = useRef<SVGSVGElement>(null)
	const dragRef = useRef<{x: number; clientX: number; moved: boolean; view: [number, number]} | null>(null)

	useEffect(() => setView([bounds[0], bounds[1]]), [bounds])

	const [v0, v1] = view
	const span = v1 - v0
	const xt = useCallback((t: number) => L + ((W - L - R) * (t - v0)) / span, [v0, span])
	const tx = useCallback((x: number) => v0 + (span * (x - L)) / (W - L - R), [v0, span])

	const setRange = useCallback((from: number, to: number) => {
		const width = Math.min(bounds[1] - bounds[0], Math.max(MIN_SPAN, to - from))
		let start = Math.max(bounds[0], from)
		if (start + width > bounds[1]) {
			start = bounds[1] - width
		}
		setView([start, start + width])
	}, [bounds])

	const toSvgX = (clientX: number) => {
		const rect = svgRef.current!.getBoundingClientRect()
		return ((clientX - rect.left) * W) / rect.width
	}

	const zoomAround = (t: number, factor: number) => {
		const width = Math.min(bounds[1] - bounds[0], Math.max(MIN_SPAN, span * factor))
		const start = t - ((t - v0) * width) / span
		setRange(start, start + width)
	}

	useEffect(() => {
		const svg = svgRef.current
		if (!svg) {
			return
		}
		const onWheel = (event: WheelEvent) => {
			const x = toSvgX(event.clientX)
			if (x < L || x > W - R) {
				return
			}
			event.preventDefault()
			zoomAround(tx(x), event.deltaY > 0 ? 1.25 : 0.8)
		}
		svg.addEventListener('wheel', onWheel, {passive: false})
		return () => svg.removeEventListener('wheel', onWheel)
	})

	const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
		dragRef.current = {x: toSvgX(event.clientX), clientX: event.clientX, moved: false, view}
	}

	const onPointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
		const drag = dragRef.current
		dragRef.current = null
		if (drag && !drag.moved) {
			const x = toSvgX(event.clientX)
			if (x >= L && x <= W - R) {
				showTip(event, x)
			}
		}
	}

	const showTip = (event: React.PointerEvent<SVGSVGElement>, x: number) => {
		if (!frames.length && !passed.length) {
			return
		}
		const t = tx(x)
		const frame = frames.length ? frames.reduce((p, c) => (Math.abs(c[0] - t) < Math.abs(p[0] - t) ? c : p)) : null
		const stop = passed.length ? passed.reduce((p, c) => (Math.abs(c.realArrival! - t) < Math.abs(p.realArrival! - t) ? c : p)) : null
		const vehicle = vypusk.vehicles.find((v) => t >= v.from && t <= v.to) ?? vypusk.vehicles[0]
		const lines = [
			stop ? `${cleanName(stop.name)} · коло ${stop.circle}${vehicle ? ' · ' + vehicle.licencePlate : ''}` : '',
			stop ? `прибуття ${formatTime(stop.realArrival)}${stop.realDeparture ? ' · відправлення ' + formatTime(stop.realDeparture) : ''}` : '',
			stop ? `план ${formatTime(stop.scheduleArrival)}` : '',
			frame ? `${formatTime(frame[0])} · ${frame[1]} пас.` : '',
		].filter(Boolean)
		const rect = svgRef.current!.getBoundingClientRect()
		setTip({left: event.clientX - rect.left + 14, top: event.clientY - rect.top + 14, lines})
	}

	const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
		const x = toSvgX(event.clientX)
		const drag = dragRef.current
		if (drag && !drag.moved && Math.abs(event.clientX - drag.clientX) >= TAP_DISTANCE) {
			drag.moved = true
			svgRef.current!.setPointerCapture(event.pointerId)
		}
		if (drag && drag.moved) {
			const width = drag.view[1] - drag.view[0]
			const shift = ((drag.x - x) * width) / (W - L - R)
			setRange(drag.view[0] + shift, drag.view[0] + shift + width)
			setTip(null)
			return
		}
		if (drag) {
			return
		}
		if (x < L || x > W - R) {
			setTip(null)
			return
		}
		showTip(event, x)
	}

	const visibleFrames = frames.filter((f) => f[0] >= v0 - 120 && f[0] <= v1 + 120)
	const vehicleIndex = (t: number) => vypusk.vehicles.findIndex((v) => t >= v.from && t <= v.to)
	const segments: ChartFrame[][] = []
	visibleFrames.forEach((frame, i) => {
		const prev = visibleFrames[i - 1]
		if (!prev || frame[0] - prev[0] > FRAME_GAP || vehicleIndex(frame[0]) !== vehicleIndex(prev[0])) {
			segments.push([])
		}
		segments[segments.length - 1].push(frame)
	})
	const segmentPath = (segment: ChartFrame[]) =>
		'M' + segment.map(([t, v]) => `${xt(t).toFixed(1)},${yy(v).toFixed(1)}`).join('L')
	const visibleStops = placed.filter((s) => s.at != null && s.at >= v0 && s.at <= v1)
	const labels = pickStopLabels(visibleStops.map((s) => ({x: xt(s.at!), ep: s.ep})))
	const showNames = span < NAMES_SPAN

	return (
		<Box>
			<Box sx={sx.toolbar}>
				<Button size="small" variant="outlined" sx={sx.button} onClick={() => zoomAround((v0 + v1) / 2, 1 / 0.6)}>−</Button>
				<Button size="small" variant="outlined" sx={sx.button} onClick={() => zoomAround((v0 + v1) / 2, 0.6)}>+</Button>
				<Button size="small" variant="outlined" sx={sx.button} onClick={() => setRange(bounds[0], bounds[1])}>Уся зміна</Button>
				{circles.filter((c) => c.count >= MIN_CIRCLE_STOPS).map((c) => (
					<Button key={c.circle} size="small" variant="outlined" sx={sx.button}
							onClick={() => setRange(c.from - EDGE, c.to + EDGE)}>
						Коло {c.circle}
					</Button>
				))}
				<Typography component="span" sx={sx.range}>
					{span >= bounds[1] - bounds[0] - 1 ? 'Уся зміна' : `${formatTime(v0)} – ${formatTime(v1)}`}
				</Typography>
			</Box>

			{!frames.length && <Typography sx={sx.note}>Немає даних підрахунку</Typography>}

			<Box sx={sx.stage}>
				<svg
					ref={svgRef}
					viewBox={`0 0 ${W} ${H}`}
					style={{display: 'block', width: '100%', height: 'auto', cursor: 'grab'}}
					onPointerDown={onPointerDown}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onPointerMove={onPointerMove}
					onPointerLeave={() => setTip(null)}
					onDoubleClick={() => setRange(bounds[0], bounds[1])}
				>
					<defs>
						<clipPath id={`plot-${vypusk.scheduleId}`}>
							<rect x={L} y={0} width={W - L - R} height={H}/>
						</clipPath>
					</defs>

					{[0, 10, 20, 30, 40, 50].map((v) => (
						<g key={v}>
							<line x1={L} x2={W - R} y1={yy(v)} y2={yy(v)} stroke="currentColor" strokeOpacity={0.08}/>
							<text x={L - 6} y={yy(v) + 4} textAnchor="end" fontSize={11} fill="currentColor" fillOpacity={0.5}>{v}</text>
						</g>
					))}

					<g clipPath={`url(#plot-${vypusk.scheduleId})`}>
						{circles.map((c) => {
							const a = xt(c.from)
							const b = xt(c.to)
							return (
								<g key={c.circle}>
									<rect x={a} y={TOP - 18} width={Math.max(2, b - a)} height={14} rx={3}
										  fill={ACCENT} fillOpacity={c.circle % 2 ? 0.12 : 0.06}/>
									<text x={Math.min(Math.max((a + b) / 2, L + 30), W - R - 30)} y={TOP - 7}
										  textAnchor="middle" fontSize={10} fill="currentColor" fillOpacity={0.6}>
										{b - a > 40 ? `Коло ${c.circle}` : c.circle}
									</text>
								</g>
							)
						})}

						{timeTicks(v0, v1).map((t) => (
							<g key={t}>
								<line x1={xt(t)} x2={xt(t)} y1={TOP} y2={PH} stroke="currentColor" strokeOpacity={0.05}/>
								<text x={xt(t)} y={PH + 14} textAnchor="middle" fontSize={10} fill="currentColor" fillOpacity={0.5}>
									{formatTime(t)}
								</text>
							</g>
						))}

						{segments.map((segment) => (
							<g key={segment[0][0]}>
								<path
									d={`${segmentPath(segment)}L${xt(segment[segment.length - 1][0])},${PH}L${xt(segment[0][0])},${PH}Z`}
									fill={ACCENT}
									fillOpacity={0.14}
								/>
								<path d={segmentPath(segment)} stroke={ACCENT} strokeWidth={1.5} fill="none"/>
							</g>
						))}
						{span < DOTS_SPAN && visibleFrames.map(([t, v]) => (
							<circle key={t} cx={xt(t)} cy={yy(v)} r={1.8} fill={ACCENT}/>
						))}

						{vypusk.vehicles.slice(1).map((v) => (
							<g key={`${v.deviceId}-${v.from}`}>
								<line x1={xt(v.from)} x2={xt(v.from)} y1={TOP} y2={PH} stroke={ACCENT} strokeWidth={2}/>
								<text x={xt(v.from) + 4} y={TOP + 12} fontSize={11} fontWeight={700} fill={ACCENT}>
									{v.licencePlate}
								</text>
							</g>
						))}

						{visibleStops.map((s, i) => {
							const x = xt(s.at!)
							if (s.ep) {
								return (
									<g key={`${s.circle}-${s.ordinal}-${i}`}>
										<line x1={x} x2={x} y1={TOP} y2={PH + 22} stroke={ACCENT} strokeDasharray="3 3" strokeOpacity={0.5}/>
										<text transform={`translate(${x + 3},${PH + 26}) rotate(55)`} fontSize={11} fontWeight={600} fill={ACCENT}>
											{`${cleanName(s.name)} ${formatTime(s.at)}${s.estimated ? ' ≈' : ''}`}
										</text>
									</g>
								)
							}
							return (
								<g key={`${s.circle}-${s.ordinal}-${i}`}>
									<line x1={x} x2={x} y1={PH} y2={PH + 4} stroke="currentColor" strokeOpacity={0.3}/>
									{showNames && labels[i] && <text transform={`translate(${x + 2},${PH + 22}) rotate(55)`}
																	 fontSize={10} fill="currentColor" fillOpacity={0.75}>
										{cleanName(s.name) + (span < NAMES_WITH_TIME_SPAN ? ' ' + formatTime(s.at) : '')}
									</text>}
								</g>
							)
						})}
					</g>
				</svg>

				{tip && <Box sx={{...sx.tip, left: tip.left, top: tip.top}}>
					{tip.lines.map((l) => <div key={l}>{l}</div>)}
				</Box>}
			</Box>
		</Box>
	)
}

export default PassengerChart
